import { css } from 'aphrodite';
import * as React from 'react';
import { useLocation } from 'react-router';
import { Button } from '../../../../web/components/Button';
import { useAISummary } from '../../../hooks/useAISummary';
import { useGetPhoneCallQuery, useGetTranscriptionSpeakerLineViewQuery } from '../../../queries';
import { useAIConversationChatMessageQuery } from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useLeadAIContext } from '../../../queries/LeadAIContext';
import { styleSheet } from './styles';

export const TestTranscriptionComponent: React.FC = () => {
	const location = useLocation();
	const [transcription, setTranscription] = React.useState<string>('');
	const [followUpQuestion, setFollowUpQuestion] = React.useState<string>('');
	const [conversationHistory, setConversationHistory] = React.useState<
		{ role: 'user' | 'assistant'; content: string }[]
	>([]);

	// Extract transcriptionId and phoneCallId from URL parameters
	const urlParams = new URLSearchParams(location.search);
	const transcriptionId = urlParams.get('transcriptionId');
	const phoneCallId = urlParams.get('phoneCallId');

	// Load transcription data if transcriptionId is provided
	const transcriptionQuery = useGetTranscriptionSpeakerLineViewQuery({
		transcriptionId: transcriptionId || '',
	});

	// Load phone call data if phoneCallId is provided
	const phoneCallQuery = useGetPhoneCallQuery({
		phoneCallId: phoneCallId || '',
	});

	// Prefetch HubSpot data when company ID is available
	const leadAIContextPrefetch = useLeadAIContext();

	// Convert transcription to array format expected by useAISummary
	const transcriptionArray = React.useMemo(() => {
		return transcription ? transcription.split('\n\n') : [];
	}, [transcription]);

	// Use the shared useAISummary hook
	const {
		aiResponse,
		isLoading,
		hasFetchedFeedback,
		handleGetPostCallFeedback,
		resetPostCallFeedback,
		sendFollowUpQuestion,
		conversationId,
	} = useAISummary(transcriptionArray, {
		phoneCallId: phoneCallId || undefined,
		companyId: phoneCallQuery.data?.companyId,
		// Note: leadServedId and source would need to be derived from other sources
		// as they're not directly on the IPhoneCall interface
	});

	// Get the full conversation history to display all messages
	const _conversationQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId || '',
	});

	// Load transcription into textarea when data is available
	React.useEffect(() => {
		if (transcriptionQuery.data?.speakerLines?.length) {
			const formattedTranscription = transcriptionQuery.data.speakerLines
				.map((line: any) => `${line.speaker}: ${line.line}`)
				.join('\n\n');
			setTranscription(formattedTranscription);
		}
	}, [transcriptionQuery.data]);

	// Prefetch HubSpot data when company ID becomes available
	React.useEffect(() => {
		if (phoneCallQuery.data?.companyId && !leadAIContextPrefetch.data && !leadAIContextPrefetch.isPending) {
			leadAIContextPrefetch.mutate({
				leadId: phoneCallQuery.data.companyId,
				// No leadServedId available in this context
			});
		}
	}, [phoneCallQuery.data?.companyId, leadAIContextPrefetch]);

	// Update conversation history when AI response completes
	React.useEffect(() => {
		if (hasFetchedFeedback && aiResponse && !isLoading) {
			setConversationHistory(prev => {
				// Check if this response is already in the history
				if (prev.length > 0 && prev[prev.length - 1].content === aiResponse) {
					return prev;
				}
				// Add the completed AI response to history
				return [...prev, { role: 'assistant', content: aiResponse }];
			});
		}
	}, [hasFetchedFeedback, aiResponse, isLoading]);

	const handleSubmit = () => {
		if (!transcription.trim()) {
			return;
		}

		// Use the shared hook to handle post-call feedback
		handleGetPostCallFeedback();
	};

	const handleReset = () => {
		resetPostCallFeedback();
		setTranscription('');
		setFollowUpQuestion('');
		setConversationHistory([]);
	};

	const handleFollowUpSubmit = () => {
		if (!followUpQuestion.trim() || !conversationId) {
			return;
		}

		// Add user's followup question to conversation history
		setConversationHistory(prev => [...prev, { role: 'user', content: followUpQuestion }]);

		sendFollowUpQuestion(followUpQuestion);
		setFollowUpQuestion('');
	};

	return (
		<div className={css(styleSheet.testContainer)}>
			<h2 className={css(styleSheet.testHeader)}>PostCall Analysis</h2>

			{phoneCallId && (
				<div className={css(styleSheet.contextInfoCard)}>
					<div className={css(styleSheet.contextInfoPrimary)}>✓ Phone Call ID: {phoneCallId}</div>
					{phoneCallQuery.data?.companyId && (
						<div className={css(styleSheet.contextInfoSecondary)}>✓ Company: {phoneCallQuery.data.companyId}</div>
					)}
					{phoneCallQuery.data?.companyId && (
						<div className={css(styleSheet.contextInfoSecondary)}>
							{leadAIContextPrefetch.data
								? '✅ HubSpot Data: Loaded'
								: leadAIContextPrefetch.isPending
									? '🔄 HubSpot Data: Loading...'
									: '❌ HubSpot Data: Not loaded'}
						</div>
					)}
					{phoneCallQuery.data && (
						<div className={css(styleSheet.contextInfoDetails)}>
							{phoneCallQuery.data.phoneNumber?.standard && <span>📞 {phoneCallQuery.data.phoneNumber.standard}</span>}
							{phoneCallQuery.data.direction && (
								<span className={css(styleSheet.contextInfoDetailItem)}>
									{phoneCallQuery.data.direction === 'Inbound' ? '📞→' : '→📞'} {phoneCallQuery.data.direction}
								</span>
							)}
							{phoneCallQuery.data.creationDate && (
								<span className={css(styleSheet.contextInfoDetailItem)}>
									📅 {new Date(phoneCallQuery.data.creationDate).toLocaleDateString()}
								</span>
							)}
						</div>
					)}
				</div>
			)}

			<div className={css(styleSheet.testToggleContainer)}>
				<span>Post-Call Analysis Mode</span>
			</div>

			<div className={css(styleSheet.testInputContainer)}>
				<label htmlFor='transcription' className={css(styleSheet.testLabel)}>
					Transcription:
				</label>
				<textarea
					id='transcription'
					value={transcription}
					onChange={e => setTranscription(e.target.value)}
					className={css(styleSheet.testTextarea)}
					placeholder='Enter call transcription...'
					rows={10}
				/>
			</div>

			<div className={css(styleSheet.headerActions)}>
				<Button onClick={handleSubmit} disabled={isLoading || !transcription.trim()}>
					Get Post-Call Analysis
				</Button>

				{(hasFetchedFeedback || conversationId) && <Button onClick={handleReset}>Reset Conversation</Button>}
			</div>

			{isLoading && (
				<div className={css(styleSheet.testLoading)}>
					{hasFetchedFeedback ? 'Receiving response...' : 'Initializing...'}
				</div>
			)}

			{(conversationHistory.length > 0 || aiResponse) && (
				<div className={css(styleSheet.testResponseContainer)}>
					<h3 className={css(styleSheet.testResponseHeader)}>
						{conversationHistory.length > 0 ? 'Conversation:' : 'AI Analysis:'}
					</h3>
					{conversationHistory.length > 0 ? (
						<div className={css(styleSheet.conversationHistory)}>
							{conversationHistory.map((message, index) => (
								<div key={index} className={css(styleSheet.conversationMessage)}>
									<div className={css(styleSheet.conversationRole)}>
										{message.role === 'user' ? '👤 You:' : '🤖 AI:'}
									</div>
									<div className={css(styleSheet.conversationText)}>{message.content}</div>
								</div>
							))}
							{isLoading && (
								<div className={css(styleSheet.conversationMessage)}>
									<div className={css(styleSheet.conversationRole)}>🤖 AI:</div>
									<div className={css(styleSheet.conversationText)}>{aiResponse || 'Thinking...'}</div>
								</div>
							)}
						</div>
					) : (
						<div className={css(styleSheet.testResponse)}>{aiResponse}</div>
					)}
				</div>
			)}

			{conversationId && (conversationHistory.length > 0 || hasFetchedFeedback) && (
				<div className={css(styleSheet.testInputContainer)}>
					<label htmlFor='followup' className={css(styleSheet.testLabel)}>
						Follow-up Question:
					</label>
					<div className={css(styleSheet.followUpContainer)}>
						<textarea
							id='followup'
							value={followUpQuestion}
							onChange={e => setFollowUpQuestion(e.target.value)}
							className={css(styleSheet.testTextarea)}
							placeholder='Ask a follow-up question...'
							rows={3}
						/>
						<Button onClick={handleFollowUpSubmit} disabled={isLoading || !followUpQuestion.trim()}>
							Ask Follow-up
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
